import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import {
  Boxes,
  Zap,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  FileText,
  Target,
  BookOpen,
  Info,
  Eye,
  ChevronRight,
  Sparkles
} from 'lucide-react';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
// All API calls must use relative URLs (/api/...) - browser uses same origin
const API_URL = '';

export default function GenerateFromRack({ examProfileId, getAuthHeaders, isActive = false }) {
  // State
  const [racks, setRacks] = useState([]);
  const [selectedRackId, setSelectedRackId] = useState('');
  const [rackPreview, setRackPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedItems, setGeneratedItems] = useState([]);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  
  // P0: run_key Filtering (Single Pipeline, Two Views)
  const [availableRunKeys, setAvailableRunKeys] = useState([]);
  const [selectedRunKey, setSelectedRunKey] = useState(''); // '' = show latest, 'all' = show all
  const [lastGeneratedRunKey, setLastGeneratedRunKey] = useState(null);
  
  // Generation options
  const [questionCount, setQuestionCount] = useState(5);
  const [selectedQuestionType, setSelectedQuestionType] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  
  // Fetch helper
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    
    return response.json();
  }, [getAuthHeaders]);
  
  // Load racks with pagination
  const loadRacks = useCallback(async () => {
    if (!examProfileId) return;
    
    setLoading(true);
    try {
      const data = await fetchWithAuth(`/api/racks?exam_profile_id=${examProfileId}&page=1&page_size=50`);
      // Handle paginated response
      setRacks(data.items || data);
    } catch (error) {
      toast.error(`Fehler beim Laden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [examProfileId, fetchWithAuth]);
  
  // Load rack preview
  const loadRackPreview = useCallback(async (rackId) => {
    if (!rackId || !examProfileId) return;
    
    try {
      const data = await fetchWithAuth(
        `/api/generate/rack-preview/${rackId}?exam_profile_id=${examProfileId}`
      );
      setRackPreview(data);
      
      // Set defaults from rack constraints
      if (data.constraints) {
        if (data.constraints.allowed_question_types?.length > 0) {
          setSelectedQuestionType(data.constraints.allowed_question_types[0]);
        }
        if (data.constraints.difficulty_band) {
          setSelectedDifficulty(data.constraints.difficulty_band);
        }
      }
    } catch (error) {
      toast.error(`Fehler beim Laden der Vorschau: ${error.message}`);
      setRackPreview(null);
    }
  }, [examProfileId, fetchWithAuth]);
  
  // Generate from rack
  const generateFromRack = async () => {
    if (!selectedRackId || !examProfileId) {
      toast.error('Bitte Rack auswählen');
      return;
    }
    
    setGenerating(true);
    try {
      // P0: Generate run_key for this generation run
      const runKey = `gen_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 6)}`;
      
      const request = {
        exam_profile_id: examProfileId,
        rack_id: selectedRackId,
        question_count: questionCount,
        run_key: runKey  // P0: Propagate run_key
      };
      
      if (selectedQuestionType) {
        request.allowed_question_types = [selectedQuestionType];
      }
      if (selectedDifficulty) {
        request.difficulty_band = selectedDifficulty;
      }
      
      const result = await fetchWithAuth('/api/generate/from-rack', {
        method: 'POST',
        body: JSON.stringify(request)
      });
      
      setLastResult(result);
      setGeneratedItems(result.items || []);
      setLastGeneratedRunKey(runKey);
      setSelectedRunKey(runKey); // Auto-filter to newly generated items
      setShowResultDialog(true);
      
      // Reload run_keys to include new one
      await loadRunKeys();
      
      toast.success(`${result.items_generated} Fragen generiert! (Run: ${runKey})`);
    } catch (error) {
      toast.error(`Fehler: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };
  
  // P0: Load available run_keys for filtering
  const loadRunKeys = useCallback(async () => {
    if (!examProfileId) return;
    
    try {
      const data = await fetchWithAuth(
        `/api/generate/run-keys?exam_profile_id=${examProfileId}`
      );
      setAvailableRunKeys(data.run_keys || []);
      
      // Auto-select latest if not already set
      if (!selectedRunKey && data.run_keys?.length > 0) {
        setSelectedRunKey(data.run_keys[0]);
      }
    } catch (error) {
      // Endpoint may not exist yet, silently ignore
      setAvailableRunKeys([]);
    }
  }, [examProfileId, fetchWithAuth, selectedRunKey]);
  
  // Load generated items (with run_key filtering)
  const loadGeneratedItems = useCallback(async () => {
    if (!examProfileId) return;
    
    try {
      let endpoint = `/api/generate/items?exam_profile_id=${examProfileId}&page_size=50`;
      
      // P0: Apply run_key filter if set
      if (selectedRunKey && selectedRunKey !== 'all') {
        endpoint += `&run_key=${selectedRunKey}`;
      }
      
      const data = await fetchWithAuth(endpoint);
      const items = data.items || data;
      setGeneratedItems(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Load items error:', error);
    }
  }, [examProfileId, fetchWithAuth, selectedRunKey]);
  
  // Effects - Lazy load only when tab is active
  useEffect(() => {
    // Only load data when tab becomes active AND we haven't loaded yet
    if (isActive && examProfileId && !initialLoadDone) {
      loadRacks();
      loadRunKeys();
      loadGeneratedItems();
      setInitialLoadDone(true);
    }
  }, [isActive, examProfileId, initialLoadDone, loadRacks, loadRunKeys, loadGeneratedItems]);
  
  // Reload items when run_key filter changes
  useEffect(() => {
    if (initialLoadDone) {
      loadGeneratedItems();
    }
  }, [selectedRunKey, loadGeneratedItems, initialLoadDone]);
  
  // Reset initialLoadDone when examProfileId changes
  useEffect(() => {
    setInitialLoadDone(false);
    setSelectedRunKey('');
    setAvailableRunKeys([]);
  }, [examProfileId]);
  
  useEffect(() => {
    if (selectedRackId) {
      loadRackPreview(selectedRackId);
    } else {
      setRackPreview(null);
    }
  }, [selectedRackId, loadRackPreview]);
  
  // Render difficulty badge
  const renderDifficultyBadge = (difficulty) => {
    const colors = {
      EASY: 'bg-green-100 text-green-700 border-green-200',
      MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      HARD: 'bg-orange-100 text-orange-700 border-orange-200',
      EXPERT: 'bg-red-100 text-red-700 border-red-200'
    };
    return (
      <Badge variant="outline" className={colors[difficulty] || 'bg-gray-100'}>
        {difficulty}
      </Badge>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="generate-from-rack">
      {/* Info Banner */}
      <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-orange-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-orange-800 dark:text-orange-200">
                Generate from Rack
              </p>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                Blueprint-driven Generierung mit systematischen Distraktoren aus dem Ontologie-Graph.
                Alle Items erhalten vollständige Provenance (rack_id, AKU-Referenzen, Evidence).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Rack Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Boxes className="w-5 h-5 text-indigo-600" />
            Rack auswählen
          </CardTitle>
          <CardDescription>
            Wählen Sie ein Rack als Basis für die Fragen-Generierung
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select 
            value={selectedRackId || 'none'} 
            onValueChange={(v) => setSelectedRackId(v === 'none' ? '' : v)}
          >
            <SelectTrigger className="w-full" data-testid="rack-select">
              <SelectValue placeholder="Rack auswählen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Rack auswählen --</SelectItem>
              {racks.map(rack => (
                <SelectItem key={rack.rack_id} value={rack.rack_id}>
                  {rack.focus_concept_names?.join(', ') || rack.rack_id} 
                  ({rack.ontology_version_number})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Rack Preview */}
          {rackPreview && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-indigo-600" />
                <span className="font-medium text-indigo-800">Rack Vorschau</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Focus Concepts</p>
                  <p className="font-medium">{rackPreview.focus_concepts?.join(', ')}</p>
                </div>
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Version</p>
                  <p className="font-mono">{rackPreview.ontology_version_number}</p>
                </div>
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Gültige AKUs</p>
                  <p className="font-medium">{rackPreview.preview?.valid_akus_count || 0}</p>
                </div>
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Distractor-Kandidaten</p>
                  <p className="font-medium">{rackPreview.preview?.distractor_candidates_count || 0}</p>
                </div>
              </div>
              
              {rackPreview.constraints && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {renderDifficultyBadge(rackPreview.constraints.difficulty_band)}
                  {rackPreview.constraints.allowed_question_types?.map(t => (
                    <Badge key={t} variant="outline">{t}</Badge>
                  ))}
                  {rackPreview.constraints.risk_flags?.map(f => (
                    <Badge key={f} variant="outline" className="text-amber-600 border-amber-300">
                      {f}
                    </Badge>
                  ))}
                </div>
              )}
              
              {!rackPreview.can_generate && (
                <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Generierung nicht möglich: {rackPreview.warnings?.join(', ')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Generation Options */}
      {selectedRackId && rackPreview?.can_generate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              Generierungs-Optionen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Question Count */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Anzahl Fragen: {questionCount}</label>
              <Slider
                value={[questionCount]}
                onValueChange={([v]) => setQuestionCount(v)}
                min={1}
                max={20}
                step={1}
                data-testid="question-count-slider"
              />
            </div>
            
            {/* Question Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Fragen-Typ</label>
              <Select 
                value={selectedQuestionType || 'default'} 
                onValueChange={(v) => setSelectedQuestionType(v === 'default' ? '' : v)}
              >
                <SelectTrigger data-testid="question-type-select">
                  <SelectValue placeholder="Standard aus Rack" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard aus Rack</SelectItem>
                  <SelectItem value="SINGLE_CHOICE">Single Choice</SelectItem>
                  <SelectItem value="MULTIPLE_CHOICE">Multiple Choice</SelectItem>
                  <SelectItem value="TRUE_FALSE">Wahr/Falsch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Difficulty */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Schwierigkeit</label>
              <Select 
                value={selectedDifficulty || 'default'} 
                onValueChange={(v) => setSelectedDifficulty(v === 'default' ? '' : v)}
              >
                <SelectTrigger data-testid="difficulty-select">
                  <SelectValue placeholder="Standard aus Rack" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard aus Rack</SelectItem>
                  <SelectItem value="EASY">Leicht</SelectItem>
                  <SelectItem value="MEDIUM">Mittel</SelectItem>
                  <SelectItem value="HARD">Schwer</SelectItem>
                  <SelectItem value="EXPERT">Experte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Info about constraints */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Hard Rules (automatisch):</p>
                  <ul className="mt-1 text-xs space-y-1">
                    <li>• Ontology-Version muss übereinstimmen</li>
                    <li>• Keine AMBIGUOUS/CONFLICTING AKUs</li>
                    <li>• Distraktoren nur aus Rack-Kandidaten</li>
                    <li>• Vollständige Provenance in Items</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Generate Button */}
            <Button
              className="w-full bg-orange-600 hover:bg-orange-700"
              onClick={generateFromRack}
              disabled={generating || !rackPreview?.can_generate}
              data-testid="generate-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generiere...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate from Rack
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* P0: Run Key Filter - Single Pipeline, Two Views */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Generierte Items Filter
          </CardTitle>
          <CardDescription>
            Filter nach Generierungs-Run (Happy Path & Manuell teilen dieselben Items)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select 
              value={selectedRunKey || 'latest'} 
              onValueChange={(v) => setSelectedRunKey(v === 'latest' ? '' : v)}
            >
              <SelectTrigger className="w-[280px]" data-testid="run-key-filter">
                <SelectValue placeholder="Filter nach Run Key" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Neueste anzeigen</SelectItem>
                <SelectItem value="all">Alle anzeigen</SelectItem>
                {availableRunKeys.map((key) => (
                  <SelectItem key={key} value={key}>
                    <span className="font-mono text-xs">{key}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                loadRunKeys();
                loadGeneratedItems();
              }}
              data-testid="refresh-items-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Aktualisieren
            </Button>
            
            {lastGeneratedRunKey && (
              <Badge variant="outline" className="bg-green-50 text-green-700">
                Letzter Run: {lastGeneratedRunKey}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Generated Items List */}
      {generatedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              Generierte Fragen ({generatedItems.length})
              {selectedRunKey && selectedRunKey !== 'all' && (
                <Badge variant="outline" className="ml-2 text-xs font-mono">
                  Run: {selectedRunKey}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {generatedItems.map((item, index) => (
                  <div 
                    key={item.item_id}
                    className="p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-sm">
                          {index + 1}. {item.question_text?.substring(0, 100)}...
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {item.question_type}
                          </Badge>
                          {renderDifficultyBadge(item.difficulty_band)}
                          {item.run_key && (
                            <Badge variant="outline" className="text-xs font-mono bg-blue-50">
                              {item.run_key}
                            </Badge>
                          )}
                          <Badge 
                            variant="outline" 
                            className={item.status === 'APPROVED' ? 'text-green-600' : item.status === 'REJECTED' ? 'text-red-600' : 'text-gray-600'}
                          >
                            {item.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.source_aku_ids?.length || 0} AKUs
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
      
      {/* Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Generierung abgeschlossen
            </DialogTitle>
            <DialogDescription>
              {lastResult?.items_generated || 0} Fragen wurden generiert
            </DialogDescription>
          </DialogHeader>
          
          {lastResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{lastResult.items_generated}</p>
                  <p className="text-sm text-green-600">Fragen</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{lastResult.akus_used}</p>
                  <p className="text-sm text-blue-600">AKUs genutzt</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{lastResult.distractors_used}</p>
                  <p className="text-sm text-purple-600">Distraktoren</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-700">{lastResult.evidence_chunks_used}</p>
                  <p className="text-sm text-amber-600">Evidence Chunks</p>
                </div>
              </div>
              
              {/* Provenance Info */}
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium mb-2">Provenance:</p>
                <div className="space-y-1 text-xs">
                  <p><strong>Rack:</strong> {lastResult.rack_id}</p>
                  <p><strong>Ontology Version:</strong> {lastResult.ontology_version_number}</p>
                  <p><strong>Difficulty:</strong> {lastResult.difficulty_band}</p>
                  <p><strong>Question Types:</strong> {lastResult.question_types_used?.join(', ')}</p>
                </div>
              </div>
              
              {/* Warnings */}
              {lastResult.warnings?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium text-amber-800 mb-1">Warnungen:</p>
                  <ul className="text-sm text-amber-700 list-disc list-inside">
                    {lastResult.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Items Preview */}
              <ScrollArea className="h-[200px]">
                <div className="space-y-2">
                  {lastResult.items?.map((item, i) => (
                    <div key={item.item_id} className="p-2 bg-white border rounded text-sm">
                      <p className="font-medium">{i + 1}. {item.question_text}</p>
                      <p className="text-green-600 mt-1">✓ {item.correct_answer}</p>
                      {item.distractors?.length > 0 && (
                        <p className="text-gray-500">✗ {item.distractors.join(', ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowResultDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
